import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import moment from 'moment';

import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { ToastService } from '@app/core/services/toast.service';
import { FormHelper } from '@app/ui-forms/classes/form-helper';
import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { PostcodeLookupService } from '@app/core/services/postcode-lookup/postcode-lookup.service';

@Component({
	selector: 'cng-configure-delivery-form',
	templateUrl: './delivery-form.component.html',
	styleUrls: ['./delivery-form.component.less'],
})
export class ConfigureDeliveryFormComponent implements OnInit {
	address: any = this.configure_service.configure_data.steps.delivery_options.data.delivery.address;
	delivery_form: FormGroup;
	delivery_time_options = [
		{ value: '' },
		{ value: '10:00', name: 'Morning' },
		{ value: '14:00', name: 'Afternoon' },
	];
	lead_days: number = this.configure_service.configure_data.steps.delivery_options.data.delivery.lead_days;
	delivery_date_min: string = moment().add(this.lead_days, 'days').format('yyyy-MM-DD');
	submitting: boolean = false;
	unavailable_days: string[] = this.configure_service.configure_data.steps.delivery_options.data.delivery.unavailable_days;
	unavailable_dates: string[] = this.configure_service.configure_data.steps.delivery_options.data.delivery.unavailable_dates;
	search_timeout: any;
    addresses: string[] = [];
	searching: boolean = false;
	tracking_log: string[] = [];

	constructor(
		private configure_service: ConfigureService,
		private toast_service: ToastService,
		private tracking_service: TrackingService,
        private postcode_lookup_service: PostcodeLookupService,
	) {
        /**
         * hardcoded for now
         */
        // this.postcode_lookup_service.setProvider('loqate');
        this.postcode_lookup_service.setProvider('getAddress');
    }

	ngOnInit(): void {
		this.delivery_form = new FormGroup({
			delivery_date: new FormControl('', Validators.required),
			delivery_time: new FormControl('', Validators.required),
			search: new FormControl(''),
			line_1: new FormControl(this.address.line_1, Validators.required),
			line_2: new FormControl(this.address.line_2),
			line_3: new FormControl(this.address.line_3),
			line_4: new FormControl(this.address.line_4),
			postcode: new FormControl(this.address.postcode, Validators.required),
		});
	}

	onAddressSearch(): void {
		clearTimeout(this.search_timeout);

        this.search_timeout = setTimeout(() => {
            const search_term = this.delivery_form.value.search;
            if (search_term.length > 3) {
                this.postcode_lookup_service.searchAddress(search_term).subscribe((response) => {
                    this.addresses = this.postcode_lookup_service.formatSuggestedAddresses(response);
                });

                return;
            }
            this.addresses = [];
        }, 1000);
	}

	onSelectAddress(id: string): void {
		this.trackAddressSelection();
		this.postcode_lookup_service.getAddressById(id).subscribe((response) => {
            this.addresses = [];
            const address = this.postcode_lookup_service.formatAddress(response);
            this.delivery_form.controls.line_1.setValue(address.line1);
            this.delivery_form.controls.line_2.setValue(address.line2);
            this.delivery_form.controls.line_3.setValue(address.line3);
            this.delivery_form.controls.line_4.setValue(address.city);
            this.delivery_form.controls.postcode.setValue(address.postcode);
        });
	}

	onCloseAddresses(): void {
		this.addresses = [];
		this.delivery_form.controls['search'].setValue(null);
	}

	onSubmit(): void {
		if (this.submitting) {
			return;
		}

		this.tracking_service.trackFormSubmitAttempt(
			'delivery_form',
			FormHelper.getCompletedControls(this.delivery_form),
			FormHelper.getEmptyControls(this.delivery_form)
		);
		if (this.delivery_form.invalid) {
			FormHelper.forceValidate(this.delivery_form);
			this.tracking_service.trackFormValidationFailure(
				'delivery_form',
				'invalid_fields[' + FormHelper.getInvalidControls(this.delivery_form).join(',') + ']'
			);
			this.toast_service.error('Form has invalid fields');
		} else {
			this.tracking_service.trackFormValidationPass('delivery_form');
			this.submitting = true;

			const address = {
				line_1: this.delivery_form.value.line_1 || null,
				line_2: this.delivery_form.value.line_2 || null,
				line_3: this.delivery_form.value.line_3 || null,
				line_4: this.delivery_form.value.line_4 || null,
				postcode: this.delivery_form.value.postcode || null,
			};

			const delivery_date_time =
				this.delivery_form.value.delivery_date + ' ' + this.delivery_form.value.delivery_time;

			this.configure_service.saveDelivery(delivery_date_time, address).subscribe((response) => {
				this.submitting = false;
				if (response) {
					this.toast_service.success('Your delivery request was sent successfully');
					this.configure_service.track('delivery.requested', { address, delivery_date: delivery_date_time }).subscribe();
					this.tracking_service.trackFormSubmitSuccess('delivery_form');
				} else {
					this.tracking_service.trackFormSubmitFailure('delivery_form');
					this.toast_service.error('An error occurred when submitting your request');
				}
			});
		}
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	trackAddressSelection(): void {
		const log_name: string = `delivery_form__address_picker__search`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_delivery_form', 'address_picker', 'search');
			this.tracking_log.push(log_name);
		}
	}
}
